// This file configures the initialization of Sentry for edge features (middleware, edge routes, and so on).
// The config you add here will be used whenever one of the edge features is loaded.
// Note that this config is unrelated to the Vercel Edge Runtime and is also required when running locally.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { BrowserOptions, ErrorEvent, EventHint } from '@sentry/nextjs'

import { isPrd } from '@shared/util/env'

const dsn =
    process.env.SENTRY_DSN || "https://8757ab63d5a842d69add259495c1327a@o177913.ingest.us.sentry.io/1385134"

function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException
  const message = error instanceof Error ? error.message : String(error || '')

  // Filter transient 'aborted' errors (Sentry issue WEB-AR4)
  if (/\baborted\b/i.test(message)) {
    return null
  }

  // Filter expected card payment errors (Sentry issue WEB-ASF)
  // These are user-facing errors (card declined, invalid card, etc.) not system failures
  const cardErrorCodes = [
    'CARD_DECLINED',
    'CARD_NUMBER_INVALID',
    'INVALID_CARD_TYPE',
    'UNSUPPORTED_CARD',
    'CVV_REQUIRED',
    'CVV_NOT_MATCHED',
    'AVS_NOT_MATCHED',
  ]
  if (cardErrorCodes.some(code => message.includes(code))) {
    return null
  }

  // Filter XMLHttpRequestProgressEvent from mobile network instability (Sentry issue WEB-ANQ)
  // These occur when XHR calls from third-party SDKs (Branch, Braze, Mixpanel) get
  // aborted due to network interruptions on Mobile Safari/iOS
  if (message.includes('XMLHttpRequestProgressEvent')) {
    return null
  }

  // Filter webkit messageHandlers errors from third-party scripts in iOS webviews (Sentry issue WEB-AWP)
  // These occur when external scripts (GTM, analytics) access window.webkit.messageHandlers
  // without null checking in Facebook's in-app browser on iOS
  if (message.includes('webkit.messageHandlers')) {
    return null
  }

  return event
}

export const config: BrowserOptions = {
  dsn,
  enabled: isPrd(),
  environment: process.env.SENTRY_ENVIRONMENT,
  tracesSampleRate: 0.1,
  // ignore api routes
  ignoreTransactions: [/^\/api(\/.*)?$/],
  beforeSend,
}
